import type React from "react";
import {Handle, type NodeProps, Position} from "@xyflow/react";
import {Checkbox, IconButton, Stack, Typography} from "@mui/material";
import {Add} from "@mui/icons-material";
import ChevronIcon from "~/icons/svg/ChevronIcon.svg?react";
import DeleteIcon from "~/icons/svg/DeleteIcon.svg?react";
import EditIcon from "~/icons/svg/EditIcon.svg?react";
import {Tag} from "~/components/tag/Tag";
import {ClickToEdit} from "~/components/ClickToEdit/ClickToEdit";
import {COLORS} from "~/style/colors-ts";
import type {TaxonomyTreeNode} from "./taxonomyTreeLayout";

const HIDDEN_HANDLE: React.CSSProperties = {opacity: 0, pointerEvents: 'none'};
/** The add / edit description / delete buttons, revealed while the card is hovered. */
const ACTIONS_CLASS = 'taxonomy-tree-node-actions';


export const TaxonomyTreeNodeCard: React.FC<NodeProps<TaxonomyTreeNode>> = ({data, draggable, dragging}) => {
    const {
        label, level, childCount, expanded, focused, onAdd, onDelete, onRename, onEditDescription, dropTarget,
        dropTargetScale, mergeSelect,
    } = data;
    const isRoot = level === 0;
    const hasChildren = childCount > 0;
    const childLabel = `${childCount} sub`;
    // Drop target while another card is dragged over it; otherwise the level filters' focus.
    const outline = dropTarget
        ? `3px dashed ${dropTarget === 'valid' ? COLORS.success.main : COLORS.error.main}`
        : focused ? `3px solid ${COLORS.primary[700]}` : undefined;

    return (
        <Stack
            justifyContent="center"
            gap={0.5}
            sx={{
                height: '100%',
                px: 1.75,
                border: '1.5px solid',
                borderColor: isRoot ? 'primary.main' : 'mithraBlue.500',
                borderRadius: '12px',
                bgcolor: isRoot ? 'primary.main' : 'common.white',
                color: isRoot ? 'primary.contrastText' : 'text.primary',
                position: 'relative',
                outline,
                outlineOffset: 3,
                cursor: dragging ? 'grabbing' : hasChildren ? 'pointer' : draggable ? 'grab' : 'default',
                opacity: dragging ? 0.85 : 1,
                boxShadow: dragging ? 4 : undefined,
                // The drop target grows (more so when zoomed out), so it's clear where the card will go.
                transform: dropTargetScale ? `scale(${dropTargetScale})` : undefined,
                transition: 'box-shadow 150ms, transform 150ms',
                '&:hover': hasChildren && !dragging ? {boxShadow: 2} : undefined,
                [`&:hover .${ACTIONS_CLASS}, & .${ACTIONS_CLASS}:focus-within`]: {opacity: 1},
            }}
        >
            <Handle type="target" position={Position.Left} isConnectable={false} style={HIDDEN_HANDLE}/>
            {dropTarget && (
                // What dropping here does, pinned to the card's top edge (as in the canvas prototype).
                <Tag
                    label={dropTarget === 'valid' ? 'Move under' : 'Name already used'}
                    color={dropTarget === 'valid' ? 'success' : 'error'}
                    sx={{position: 'absolute', top: -12, right: 8, height: 20, minHeight: 20, fontSize: 11, fontWeight: 600}}
                />
            )}
            <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
                {mergeSelect && (
                    // Merge mode: tick the categories to merge; a click here doesn't open / close the card.
                    <Checkbox
                        size="small"
                        checked={mergeSelect.checked}
                        disabled={mergeSelect.disabled}
                        onChange={mergeSelect.onToggle}
                        onClick={e => e.stopPropagation()}
                        className="nodrag nopan"
                        title={mergeSelect.disabled ? 'Only categories under the same parent can be merged' : undefined}
                        slotProps={{input: {'aria-label': `Select ${label} to merge`}}}
                        sx={{p: 0, ml: -0.5}}
                    />
                )}
                {!isRoot && (
                    <Tag
                        label={`L${level}`}
                        color="default"
                        variant="outlined"
                        size="small"
                        sx={{flexShrink: 0, minHeight: '14px', '&&': {borderRadius: 0.5}}}
                    />
                )}
                {onRename
                    // Clicking the name edits it (the canvas confirms it, with a description, before saving);
                    // clicking the rest of the card opens / closes it.
                    ? (
                        <ClickToEdit
                            text={label}
                            onSave={onRename}
                            required
                            className="nodrag nopan"
                            typographyProps={{fontSize: 13.5, fontWeight: 600, title: label}}
                            inputSx={{fontSize: 13.5, fontWeight: 600}}
                            sx={{flex: 1, minWidth: 0, ml: -0.5, py: 0}}
                        />
                    )
                    : (
                        <Typography fontSize={13.5} fontWeight={600} noWrap title={label} flex={1} minWidth={0}>
                            {label}
                        </Typography>
                    )}
            </Stack>
            <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
                {isRoot && (
                    <Typography variant="caption" noWrap sx={{color: 'inherit', opacity: 0.8}}>Taxonomy</Typography>
                )}
                {(onAdd || onEditDescription || onDelete) && (
                    // Shown on hover; a click here neither pans the canvas nor opens / closes the branch.
                    <Stack
                        direction="row"
                        className={`${ACTIONS_CLASS} nodrag nopan`}
                        sx={{flexShrink: 0, ml: isRoot ? 0 : -0.25, opacity: 0, transition: 'opacity 120ms'}}
                        onClick={e => e.stopPropagation()}
                    >
                        {onAdd && (
                            <IconButton
                                size="small"
                                aria-label={isRoot ? 'Add L1 category' : `Add sub-category to ${label}`}
                                title={isRoot ? 'Add L1 category' : 'Add sub-category'}
                                onClick={onAdd}
                                sx={{color: 'inherit', p: 0.375}}
                            >
                                <Add sx={{fontSize: 22}}/>
                            </IconButton>
                        )}
                        {onEditDescription && (
                            <IconButton
                                size="small"
                                aria-label={`Edit description of ${label}`}
                                title="Edit description"
                                onClick={onEditDescription}
                                sx={{color: 'inherit', p: 0.375}}
                            >
                                <EditIcon width={22} height={22}/>
                            </IconButton>
                        )}
                        {onDelete && (
                            <IconButton
                                size="small"
                                aria-label={`Delete ${label}`}
                                title="Delete"
                                onClick={onDelete}
                                sx={{color: 'inherit', p: 0.375, '&:hover': {color: 'error.main'}}}
                            >
                                <DeleteIcon width={22} height={22}/>
                            </IconButton>
                        )}
                    </Stack>
                )}
                {hasChildren && (
                    <Stack
                        direction="row"
                        alignItems="center"
                        flexShrink={0}
                        title={`Click to ${expanded ? 'hide' : 'show'} ${childCount} sub-categories`}
                        sx={{
                            // Pushed to the right, past the add / delete buttons.
                            ml: 'auto',
                            pl: 0.75,
                            pr: 0.25,
                            border: '1px solid',
                            borderRadius: '10px',
                            ...(isRoot
                                ? {borderColor: 'primary.contrastText', color: 'inherit'}
                                : {
                                    borderColor: expanded ? COLORS.primary[400] : 'grey.200',
                                    color: expanded ? COLORS.primary[700] : 'text.secondary',
                                    bgcolor: expanded ? COLORS.primary[100] : 'common.white',
                                }),
                        }}
                    >
                        <Typography variant="caption" fontWeight={600}>{childLabel}</Typography>
                        <ChevronIcon
                            width={16}
                            height={16}
                            style={{transform: expanded ? 'rotate(90deg)' : undefined, flexShrink: 0}}
                        />
                    </Stack>
                )}
            </Stack>
            <Handle type="source" position={Position.Right} isConnectable={false} style={HIDDEN_HANDLE}/>
        </Stack>
    );
};
